import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SealCheck, WarningCircle, Recycle, PenNib } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/SignaturePad";
import { brandVars } from "../lib/brandTheme";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const PLATFORM_MARK = "/icon-192x192.png";
const brandLogo = (url) => (url ? (url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : PLATFORM_MARK);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

function Shell({ brand = {}, children }) {
  const name = brand.company_name || "Fixlr";
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" style={brandVars(brand.primary_color)} data-testid="wtn-sign-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <img src={brandLogo(brand.logo_url)} alt={name} className="h-14 mx-auto object-contain" onError={(e) => { if (!e.currentTarget.src.endsWith(PLATFORM_MARK)) e.currentTarget.src = PLATFORM_MARK; }} />
          <p className="mt-2 text-sm font-semibold text-slate-700" data-testid="wtn-sign-brand">{name}</p>
        </div>
        {children}
        <p className="text-center text-[11px] text-slate-400 mt-6">{brand.footer_text || brand.tagline || name}</p>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return v ? <div className="flex gap-3 text-sm"><span className="w-32 shrink-0 text-slate-500">{k}</span><span className="text-slate-800">{v}</span></div> : null;
}

/* Public page at /sign/wtn/:token — the waste producer reviews the note and signs as transferor from their own phone. */
export default function WtnSignPage() {
  const { token } = useParams();
  const [note, setNote] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    const root = document.documentElement, wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => { if (wasDark) root.classList.add("dark"); };
  }, []);
  useEffect(() => {
    API.get(`/public/pops/wtn/${token}`).then((r) => setNote(r.data)).catch((e) => setError(e?.response?.data?.detail || "This link isn't valid any more."));
  }, [token]);

  const submit = async () => {
    if (name.trim().length < 2 || !sig) { toast.error("Please print your name and sign in the box"); return; }
    setBusy(true);
    try { const r = await API.post(`/public/pops/wtn/${token}/sign`, { name: name.trim(), signature: sig }); setDone(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save your signature — please try again"); } finally { setBusy(false); }
  };

  if (error) return <Shell><div className="bg-white rounded-lg shadow p-10 text-center" data-testid="wtn-sign-error"><WarningCircle size={44} className="mx-auto text-amber-500 mb-3" /><h2 className="text-lg font-semibold text-slate-800">Link unavailable</h2><p className="text-sm text-slate-500 mt-2">{error}</p></div></Shell>;
  if (!note) return <Shell><div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500" data-testid="wtn-sign-loading">Loading your transfer note…</div></Shell>;
  const brand = note.branding || {};
  const signed = done || note.signed;
  if (signed) return (
    <Shell brand={brand}>
      <div className="bg-white rounded-lg shadow p-10 text-center" data-testid="wtn-sign-done">
        <SealCheck size={52} className="mx-auto text-emerald-500 mb-3" weight="fill" />
        <h2 className="text-xl font-bold text-slate-800">{done ? "Thank you — signed" : "Already signed"}</h2>
        <p className="text-sm text-slate-500 mt-2">Waste transfer note <span className="font-mono font-semibold">{note.number}</span> {done ? "has been signed" : "was signed"}{(done ? name : note.signed_name) ? ` by ${done ? name : note.signed_name}` : ""} on {fmt(done?.signed_at || note.signed_at)}. {brand.company_name || "The carrier"} will send you the completed copy for your records.</p>
      </div>
    </Shell>
  );
  return (
    <Shell brand={brand}>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center bg-emerald-50 text-emerald-700"><Recycle size={22} /></div>
          <div><p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Waste transfer note</p><h1 className="text-lg font-bold text-slate-800 font-mono" data-testid="wtn-sign-number">{note.number}</h1></div>
          <span className="ml-auto text-[11px] text-slate-400">Expires {fmt(note.expires_at)}</span>
        </div>
        <div className="px-6 py-4 space-y-4 text-sm">
          <p className="text-slate-600">{note.carrier?.name || "The carrier"} collected the waste below from you. Please check the details, then print your name and sign as the <strong>transferor</strong> (the person handing the waste over). This is a legal record under the Duty of Care.</p>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Waste</p>
            <div className="space-y-2">
              {note.waste.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`wtn-sign-waste-${i}`}>
                  <p className="text-slate-800">{r.description}</p>
                  <p className="text-xs text-slate-500 mt-1">EWC <span className="font-mono">{r.ewc_code}</span>{r.qty ? ` · ${r.qty} item${r.qty === 1 ? "" : "s"}` : ""}{r.weight_kg ? ` · ${r.weight_kg} kg` : ""}{r.volume_m3 ? ` · ${r.volume_m3} m³` : ""}{r.containment ? ` · ${r.containment}` : ""}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Producer (you)</p><Row k="Name" v={note.producer?.name} /><Row k="Address" v={note.producer?.address} /></div>
            <div className="space-y-1"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Carrier</p><Row k="Name" v={note.carrier?.name} /><Row k="Licence" v={note.carrier?.licence_number} /><Row k="Going to" v={note.receiver?.name} /></div>
          </div>
          <div className="space-y-1"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Transfer</p><Row k="Date" v={[note.transfer?.date ? fmt(note.transfer.date) : "", note.transfer?.time].filter(Boolean).join(" at ")} /><Row k="Place" v={note.transfer?.place} /></div>
          {note.photos?.length > 0 && (
            <div data-testid="wtn-sign-photos">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Photos taken at collection</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {note.photos.map((p) => (
                  <figure key={p.id} className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`wtn-sign-photo-${p.id}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt={p.caption || "Collection photo"} className="h-28 w-full object-cover" loading="lazy" />
                    {p.caption && <figcaption className="px-2 py-1 text-[11px] text-slate-600 truncate">{p.caption}</figcaption>}
                  </figure>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 space-y-3">
          <div><Label className="text-xs text-slate-600">Your full name (printed)</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 bg-white text-slate-900 placeholder:text-slate-400" placeholder="e.g. Jane Smith" data-testid="wtn-sign-name" /></div>
          <div><Label className="text-xs text-slate-600">Sign with your finger</Label><SignaturePad value={sig} onChange={setSig} height={180} /></div>
          <Button className="w-full h-11" onClick={submit} disabled={busy} data-testid="wtn-sign-submit"><PenNib size={16} className="mr-2" /> {busy ? "Saving…" : `Sign ${note.number}`}</Button>
          <p className="text-[11px] text-slate-400 text-center">By signing you confirm the waste description is accurate and that you handed it to the carrier named above.</p>
        </div>
      </div>
    </Shell>
  );
}
